import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, FileText, SpeakerHigh, WarningCircle} from '../../../icons';
import {DOC_STATUS, docErrorParts} from '../../memory/docConstants';
import {kindLabelKey} from '../../memory/contextPackConstants';
import {fileKindOf} from '../../memory/ingestFiles';
import {FileKindIcon} from './fileKindIcon';
import {DocumentRawModal} from '../../memory/DocumentRawModal';
import {AudioNotePlayer} from '../../../audio/AudioNotePlayer';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {BOOSTER_STEP_REVIEW, boosterFlowPath} from '../boosterSteps';

// Fase visible por estado de extracción. Son los CUATRO valores del enum del backend
// (`ClientDocument.STATUS_*`: PENDING · EXTRACTING · READY · FAILED), no una máquina de estados
// paralela: el mockup fabrica las fases con `setTimeout` encadenados, y eso miente en cuanto la
// extracción tarda más (o falla) que lo que dura la animación.
const PHASE_KEY = {
    [DOC_STATUS.PENDING]: 'ia_booster.feed.ingest_queued',
    [DOC_STATUS.EXTRACTING]: 'ia_booster.feed.ingest_extracting',
    [DOC_STATUS.READY]: 'ia_booster.feed.ingest_ready',
    [DOC_STATUS.FAILED]: 'ia_booster.feed.ingest_failed',
};

const MODIFIER = {
    [DOC_STATUS.PENDING]: '-pending',
    [DOC_STATUS.EXTRACTING]: '-extracting',
    [DOC_STATUS.READY]: '-ready',
    [DOC_STATUS.FAILED]: '-failed',
};

export const isInFlight = (doc) => doc
    && (doc.status === DOC_STATUS.PENDING || doc.status === DOC_STATUS.EXTRACTING);

// El aviso de "perdimos el rastro" tiene copy propio y no reusa el de un error genérico: acá no
// falló una acción del usuario, se cortó la vigilancia de algo que probablemente terminó bien. Por
// eso el texto no pide disculpas ni promete reintentar solo — dice qué dejamos de saber y ofrece
// mirar de nuevo.
const STALLED_TEXT_KEY = 'ia_booster.feed.ingest_stalled';
const STALLED_RETRY_KEY = 'ia_booster.feed.ingest_stalled_retry';
const RESULT_KEYS = {
    SUMMARY_ONLY: 'summary_only', NO_DURABLE_FACTS: 'no_durable_facts',
    ALREADY_KNOWN: 'already_known', PREVIOUS_OUTPUT_PRESERVED: 'previous_preserved',
    FACTS_EXTRACTED: 'facts_extracted', NO_MEANINGFUL_CONTENT: 'no_meaningful_content',
};
// Dónde quedaron los hechos de este material. Cada número va en su propia clave pluralizada: una
// sola frase con tres números no se puede pluralizar (i18next elige el plural por UN `count`) y
// decía «1 aprobados». Los que dan cero no se nombran.
const REVIEW_COUNT_KEYS = ['approved', 'pending', 'noise'];

export function reviewCountsText(counts, t) {
    if (!counts) return '';
    return REVIEW_COUNT_KEYS
        .filter(key => counts[key] > 0)
        .map(key => t(`ia_booster.feed.result_${key}`, {count: counts[key]}))
        .join(' · ');
}

/**
 * Lo que está entrando a la memoria, con su estado REAL.
 *
 * Presentacional a propósito: el dueño del dato es `FeedActContent` (un solo fetch y un solo poll
 * para toda la pantalla). Acá sólo se pinta lo que llega.
 *
 * `stalled` es el caso en el que el poll se rindió (varias vueltas malas seguidas). No es un detalle
 * técnico: sin él, las filas se quedaban anunciando «Clasificando y extrayendo…» para siempre, con
 * el shimmer corriendo, sobre una extracción que nadie estaba mirando. Con él, la fase queda como
 * lo ÚLTIMO que supimos, el shimmer se apaga y abajo se dice qué pasó, con la puerta para volver a
 * mirar.
 *
 * `facts_extracted_count` lo va a exponer el backend por documento, y hasta que ese deploy salga el
 * campo no viene. Por eso el enlace a los hechos sólo se dibuja si el valor es un NÚMERO: un
 * `|| 0` pondría «0 hechos nuevos» en todos los documentos de la ventana en la que el backend
 * todavía no deployó, que es una afirmación falsa (y la peor posible: dice que Dashbo leyó y no
 * encontró nada).
 *
 * **Un material que falló ofrece las dos preguntas que el usuario se hace**: qué se escuchó
 * («Escuchar», para una nota de audio: pide una URL firmada de vida corta y monta el reproductor
 * ahí mismo) y qué se leyó («Ver lo que leímos», el crudo — para un audio, la transcripción). Sin
 * eso, un rechazo era una afirmación sin evidencia: el 2026-09-04 dos notas correctas se
 * anunciaron como «material vacío» y no había forma de comprobar nada.
 *
 * **`can_retry` lo decide el BACKEND.** La captura de ese día mostraba «Reintentar no cambia el
 * resultado» y, al lado, el botón. Con el campo ausente (backend viejo) se conserva el
 * comportamiento anterior: los dos repos no deployan juntos.
 *
 * ⚠️ **El enlace a los hechos se queda DENTRO del Recorrido.** Apuntaba a `/client/:id/memory/facts`,
 * que es una ruta legacy que hoy hace `<Navigate replace>` de vuelta a `#revisa`: el destino
 * efectivo era el mismo, pero por ser otra ruta desmontaba `ClientBoosterPage` en el camino y con
 * él se iban el undo, los contadores de la sesión, los latches de `LazySection`, el scroll y todos
 * los fetches de la página. Y esto vive en Alimenta, que es justo desde donde el usuario cruza a
 * Revisa.
 */
export function IngestActivityList({
    documents = [],
    clientId,
    onRetry,
    onReprocess,
    onCorrectKind,
    busyId = null,
    // El poll de arriba se rindió: dejamos de saber en qué estado está lo que se está extrayendo.
    // Se dibuja aunque no haya filas, porque el caso más feo es justamente ese — el primer
    // documento del cliente entró y la vuelta que tenía que traerlo nunca llegó.
    stalled = false,
    onRefresh,
}) {
    const {t} = useTranslation();
    // Una URL firmada por documento, pedida sólo cuando alguien quiere escuchar: vencen a los
    // minutos, así que traerlas con el listado sería traerlas vencidas.
    const [media, setMedia] = useState({});
    const [rawTarget, setRawTarget] = useState(null);

    const playAudio = (doc) => {
        setMedia((current) => ({...current, [doc.id]: {loading: true}}));
        ClientMemoryApi.getDocumentMediaUrl(doc.id, clientId)
            .then((data) => setMedia((current) => ({
                ...current,
                [doc.id]: data && data.url ? {url: data.url} : {error: true},
            })))
            .catch(() => setMedia((current) => ({...current, [doc.id]: {error: true}})));
    };

    if (!documents.length && !stalled) return null;

    return (
        <div className={`iab-ingest ${stalled ? '-stalled' : ''}`}>
            <h4 className="iab-ingest-title">{t('ia_booster.feed.ingest_title')}</h4>
            <ul className="iab-ingest-list">
                {documents.map((doc) => {
                    // La lectura se interrumpió (lo deduce el backend por el tiempo en
                    // EXTRACTING): se trata como un fallo con reintento, no como «en vuelo» —
                    // que es lo que la fila decía para siempre.
                    const stale = doc.stale === true;
                    const flying = isInFlight(doc) && !stale;
                    const failed = doc.status === DOC_STATUS.FAILED || stale;
                    const ready = doc.status === DOC_STATUS.READY;
                    // Un archivo subido trae `file`: el ícono dice qué es. Sin el campo, el de
                    // siempre — no se adivina por el título.
                    const fileKind = doc.file && doc.file.name
                        ? fileKindOf(doc.file.name, doc.file.mime_type) : null;
                    const result = doc.extraction_result;
                    const facts = result?.counts?.created ?? doc.facts_extracted_count;
                    const knownFacts = typeof facts === 'number';
                    const isAudio = !!(doc.file && String(doc.file.mime_type || '').startsWith('audio/'));
                    // `can_retry` ausente = backend viejo: se conserva el comportamiento de antes.
                    const canRetry = doc.can_retry !== false;
                    const audio = media[doc.id];
                    return (
                        <li key={doc.id} className={`iab-ing ${stale ? '-failed' : (MODIFIER[doc.status] || '-pending')}`}>
                            <span className="iab-ing-ic" aria-hidden="true">
                                {failed
                                    ? <WarningCircle size={16}/>
                                    : (fileKind ? <FileKindIcon kind={fileKind} size={16}/> : <FileText size={16}/>)}
                            </span>
                            <div className="iab-ing-body">
                                {/* Hasta dos renglones y, si igual no entra, el título entero al
                                    pasar el mouse: el de un texto pegado sale de su primera frase y
                                    cortado en uno no se podía leer (QA-VIS-alim-04). */}
                                <div className="iab-ing-title" title={doc.title || undefined}>{doc.title}</div>
                                <div className="iab-ing-meta">
                                    <span className="iab-ing-phase">
                                        {stale
                                            ? t('client_memory.doc_stale')
                                            : t(PHASE_KEY[doc.status] || PHASE_KEY[DOC_STATUS.PENDING])}
                                    </span>
                                    {/* El tipo con el que quedó clasificado: es la respuesta a
                                        "¿entendió qué era esto?", y es lo que el usuario mira
                                        cuando declaró un tipo a mano. */}
                                    {ready && doc.document_kind && (
                                        <span className="iab-ing-kind">
                                            {t(kindLabelKey(doc.document_kind))}
                                        </span>
                                    )}
                                    {ready && knownFacts && facts > 0 && (
                                        <Link className="iab-ing-go"
                                              to={boosterFlowPath(clientId, BOOSTER_STEP_REVIEW)}>
                                            {/* Singular y plural explícitos, sin `count`: es la
                                                misma regla que la línea de fuentes diez píxeles más
                                                abajo, que sí distinguía «1 hecho encontrado». Las
                                                dos frases se leen juntas, y decían «→ 1 hechos
                                                nuevos» al lado de «· 1 hecho encontrado». */}
                                            {facts === 1
                                                ? t('ia_booster.feed.ingest_facts_one')
                                                : t('ia_booster.feed.ingest_facts_many', {n: facts})}
                                        </Link>
                                    )}
                                    {ready && (
                                        <span className="iab-ing-nofacts">
                                            {t(`ia_booster.feed.result_${RESULT_KEYS[result?.disposition] || 'unknown'}`)}
                                        </span>
                                    )}
                                </div>
                                {/* Un `div` y no un `<p>`: el reset de párrafos de la escena le
                                    ganaba a la clase y el renglón salía a 16 px, más grande que el
                                    título (QA-VIS-alim-01). El rótulo dice que es el ESTADO de los
                                    hechos de este material, no lo que hizo esta carga: con material
                                    repetido cuenta hechos que ya estaban (QA-alim-N01 y N21). */}
                                {ready && reviewCountsText(doc.review_counts, t) && (
                                    <div className="iab-ing-meta iab-ing-counts">
                                        <span className="iab-ing-counts-label">
                                            {t('ia_booster.feed.review_counts_label')}
                                        </span>
                                        <span>{reviewCountsText(doc.review_counts, t)}</span>
                                    </div>
                                )}
                                {ready && doc.can_reprocess && onCorrectKind && (
                                    <button type="button" className="iab-ing-link"
                                            disabled={busyId === doc.id}
                                            onClick={() => onCorrectKind(doc)}>
                                        {t('ia_booster.feed.correct_kind')}
                                    </button>
                                )}
                                {/* El motivo del fallo es del backend y es accionable ("la página
                                    pide login", "el archivo vino vacío"): sin él, Reintentar es un
                                    botón a ciegas. */}
                                {failed && !stale && doc.summary_error && (() => {
                                    // Ídem la tabla clásica: el volcado del motor no va a pantalla
                                    // (QA 2026-09-07, QA-VIS-cola-06).
                                    const {message, technical} = docErrorParts(doc, t);
                                    return (
                                        <div className="iab-ing-error">
                                            {message}
                                            {technical && (
                                                <details className="iab-ing-error-detail">
                                                    <summary>{t('client_memory.doc_error_detail')}</summary>
                                                    <code>{technical}</code>
                                                </details>
                                            )}
                                        </div>
                                    );
                                })()}
                                {/* Las dos preguntas de un material rechazado: qué se escuchó y
                                    qué se leyó. Se ofrecen SIEMPRE que haya con qué contestarlas
                                    —no sólo cuando falló—: el mismo botón sirve para verificar un
                                    documento que entró bien. */}
                                {(isAudio || doc.has_raw) && (
                                    <div className="iab-ing-evidence">
                                        {isAudio && !audio && (
                                            <button type="button" className="iab-ing-link"
                                                    onClick={() => playAudio(doc)}>
                                                <SpeakerHigh size={13} aria-hidden="true"/>
                                                {t('ia_booster.feed.ingest_listen')}
                                            </button>
                                        )}
                                        {audio && audio.loading && (
                                            <span className="iab-ing-link -muted">
                                                {t('ia_booster.feed.ingest_listen_loading')}
                                            </span>
                                        )}
                                        {audio && audio.url && (
                                            <AudioNotePlayer
                                                src={audio.url}
                                                durationSeconds={doc.file && doc.file.duration_seconds}
                                                label={doc.title}/>
                                        )}
                                        {audio && audio.error && (
                                            <span className="iab-ing-link -muted" role="alert">
                                                {t('ia_booster.feed.ingest_listen_error')}
                                            </span>
                                        )}
                                        {doc.has_raw && (
                                            <button type="button" className="iab-ing-link"
                                                    onClick={() => setRawTarget(doc)}>
                                                <FileText size={13} aria-hidden="true"/>
                                                {isAudio
                                                    ? t('ia_booster.feed.ingest_read_transcript')
                                                    : t('ia_booster.feed.ingest_read_raw')}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {/* El shimmer es la señal de que algo está pasando del otro lado.
                                    Con el poll rendido no lo sabemos, y dejarlo corriendo sería la
                                    misma mentira de siempre con animación. */}
                                {flying && !stalled
                                    && <span className="iab-ing-shimmer" aria-hidden="true"/>}
                            </div>
                            {/* «Volver a procesar»: para un documento SANO que se quiere volver a
                                leer con el extractor de hoy. Nunca convive con «Reintentar» —ese es
                                para un dead-end— y sólo aparece si el backend dice que se puede
                                (`can_reprocess`: el material tiene que poder conseguirse de nuevo).
                                Confirma antes: gasta crédito de IA otra vez y puede duplicar hechos. */}
                            {ready && doc.can_reprocess && onReprocess && (
                                <button type="button" className="iab-ing-retry"
                                        disabled={busyId === doc.id}
                                        onClick={() => onReprocess(doc)}>
                                    <ArrowsClockwise size={13} aria-hidden="true"/>
                                    {busyId === doc.id
                                        ? t('ia_booster.feed.ingest_reprocessing')
                                        : t('ia_booster.feed.ingest_reprocess')}
                                </button>
                            )}
                            {failed && canRetry && (
                                <button type="button" className="iab-ing-retry"
                                        disabled={busyId === doc.id}
                                        onClick={() => onRetry && onRetry(doc)}>
                                    <ArrowsClockwise size={13} aria-hidden="true"/>
                                    {busyId === doc.id
                                        ? t('ia_booster.feed.ingest_retrying')
                                        : t('ia_booster.feed.ingest_retry')}
                                </button>
                            )}
                        </li>
                    );
                })}
            </ul>

            <DocumentRawModal
                isOpen={!!rawTarget}
                document={rawTarget}
                clientId={clientId}
                toggle={() => setRawTarget(null)}/>

            {/* Perdimos el rastro: se dice, y se ofrece volver a mirar. La alternativa es la que
                había — filas anunciando una fase que nadie está siguiendo, indefinidamente. */}
            {stalled && (
                <div className="iab-ingest-stale" role="status">
                    <span className="iab-ingest-stale-ic" aria-hidden="true">
                        <WarningCircle size={15}/>
                    </span>
                    <span className="iab-ingest-stale-txt">{t(STALLED_TEXT_KEY)}</span>
                    <button type="button" className="iab-ing-retry" onClick={onRefresh}>
                        <ArrowsClockwise size={13} aria-hidden="true"/>
                        {t(STALLED_RETRY_KEY)}
                    </button>
                </div>
            )}
        </div>
    );
}

export default IngestActivityList;
